/**
 * Redirect utility for handling external redirects with tracking parameters
 */

// Default redirect URL
const DEFAULT_REDIRECT_URL = 'https://pixellot.tv'

// Route mapping configuration
const ROUTE_MAP = {
	'analytics-campaign': {
		'learn-more': 'https://vidswap.pixellot.tv/'
	}
}

// Generate test cases dynamically from ROUTE_MAP
// This ensures TEST_CASES always stays in sync with ROUTE_MAP
export const TEST_CASES = Object.entries(ROUTE_MAP).flatMap(([source, actions]) =>
	Object.keys(actions)
		.filter(action => action !== 'default')
		.map((action, index) => ({
			id: `${source}-${action}`.replace(/[^a-zA-Z0-9]/g, '-'),
			label: `${source.charAt(0).toUpperCase() + source.slice(1).replace(/-/g, ' ')} - ${action.charAt(0).toUpperCase() + action.slice(1).replace(/-/g, ' ')}`,
			source,
			action
		}))
)

/**
 * Get redirect URL based on source and action
 * @param {string} source - The source of the redirect (e.g., 'analytics-campaign', 'popup')
 * @param {string} action - The action that triggered the redirect (e.g., 'learn-more', 'upgrade')
 * @param {Object} additionalParams - Additional query parameters to add
 * @returns {string} The complete redirect URL with tracking parameters
 */
export function getRedirectUrl (source = 'analytics-campaign', action = 'learn-more', additionalParams = {}) {
	// Get the source mapping or use default
	const sourceMap = ROUTE_MAP[source] || ROUTE_MAP['analytics-campaign']

	// Get the action URL or use default for the source
	const actionUrl = sourceMap[action] || sourceMap.default || DEFAULT_REDIRECT_URL

	// Create URL object
	const url = new URL(actionUrl)

	// Add tracking parameters
	url.searchParams.set('utm_source', source || 'unknown')
	url.searchParams.set('utm_medium', 'webapp')
	url.searchParams.set('utm_campaign', 'analytics-popup')
	url.searchParams.set('utm_content', action || 'default')
	url.searchParams.set('utm_term', 'pixellot-you')

	// Add additional parameters
	Object.entries(additionalParams).forEach(([key, value]) => {
		if (value !== null && value !== undefined) {
			url.searchParams.set(key, value)
		}
	})

	return url.toString()
}

/**
 * Navigate to external URL with tracking
 * @param {string} source - The source of the redirect
 * @param {string} action - The action that triggered the redirect
 * @param {Object} options - Navigation options
 * @param {boolean} options.newTab - Whether to open in new tab
 * @param {Object} options.additionalParams - Additional query parameters
 */
export function navigateToExternal (source, action, options = {}) {
	const { newTab = true, additionalParams = {} } = options
	const url = getRedirectUrl(source, action, additionalParams)

	if (newTab) {
		window.open(url, '_blank')
	} else {
		window.location.href = url
	}
}

/**
 * Get all available sources
 * @returns {Array<string>} Array of available source names
 */
export function getAvailableSources () {
	return Object.keys(ROUTE_MAP)
}

/**
 * Get all available actions for a specific source
 * @param {string} source - The source to get actions for
 * @returns {Array<string>} Array of available action names
 */
export function getAvailableActions (source) {
	const sourceMap = ROUTE_MAP[source]
	return sourceMap ? Object.keys(sourceMap) : []
}

/**
 * Create a redirect route object for Vue Router
 * @param {string} source - The source of the redirect (e.g., 'analytics-campaign')
 * @param {string} action - The action that triggered the redirect (e.g., 'learn-more')
 * @param {Object} additionalParams - Additional query parameters to include
 * @returns {Object} Vue Router route object
 */
export function createRedirectRoute (source = 'analytics-campaign', action = 'learn-more', additionalParams = {}) {
	return {
		name: 'link-redirect',
		query: {
			source,
			action,
			...additionalParams
		}
	}
}

export default {
	getRedirectUrl,
	createRedirectRoute,
	navigateToExternal,
	getAvailableSources,
	getAvailableActions,
	TEST_CASES
}
